import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Snowflake, TrendingUp, TrendingDown, DollarSign, Clock, Users,
  Database, AlertTriangle, Zap, ArrowRight, Search, Bell,
  Server, Activity, BarChart3, PieChart, RefreshCw, Download,
  CheckCircle, XCircle, MinusCircle, Lightbulb, ChevronRight, Plug, Loader2
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import SnowflakeConnect, { useSnowflakeCredentials } from '../components/SnowflakeConnect';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';
import axios from 'axios';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SnowflakeDashboard = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [refreshing, setRefreshing] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [loading, setLoading] = useState(false);
  const [realData, setRealData] = useState(null);
  const { credentials, isConnected, disconnect, updateCredentials } = useSnowflakeCredentials();
  const { shouldShowData } = useDemoData();
  
  // Get days from timeRange
  const getDays = () => {
    switch(timeRange) {
      case '24h': return 1;
      case '7d': return 7;
      case '30d': return 30;
      case '90d': return 90;
      default: return 7;
    }
  };

  // Fetch real data when connected
  const fetchRealData = async () => {
    if (!credentials) return;
    
    setLoading(true);
    try {
      const days = getDays();
      
      // Fetch all data in parallel
      const [costSummary, warehouseMetering, userCosts, storageUsage] = await Promise.all([
        axios.post(`${API_URL}/api/snowflake/cost-summary`, { credentials, days }),
        axios.post(`${API_URL}/api/snowflake/warehouse-metering`, { credentials, days }),
        axios.post(`${API_URL}/api/snowflake/user-costs`, { credentials, days }),
        axios.post(`${API_URL}/api/snowflake/storage-usage`, { credentials, days })
      ]);

      setRealData({
        costSummary: costSummary.data,
        warehouseMetering: warehouseMetering.data,
        userCosts: userCosts.data,
        storageUsage: storageUsage.data
      });
      
      toast.success('Data refreshed from Snowflake');
    } catch (error) {
      console.error('Failed to fetch Snowflake data:', error);
      toast.error(error.response?.data?.detail || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isConnected && credentials) {
      fetchRealData();
    }
  }, [isConnected, timeRange]);

  const handleRefresh = () => {
    if (isConnected) {
      fetchRealData();
    } else {
      setRefreshing(true);
      setTimeout(() => setRefreshing(false), 1500);
    }
  };

  const handleConnected = (creds, accountInfo) => {
    updateCredentials(creds);
    setShowConnect(false);
  };

  // Mock data - Credit usage over time (used when not connected)
  const creditUsageData = [
    { date: 'Mon', credits: 1245, cost: 3112, queries: 4521 },
    { date: 'Tue', credits: 1389, cost: 3472, queries: 5234 },
    { date: 'Wed', credits: 1567, cost: 3917, queries: 6123 },
    { date: 'Thu', credits: 2341, cost: 5852, queries: 8934 }, // Spike!
    { date: 'Fri', credits: 1456, cost: 3640, queries: 5678 },
    { date: 'Sat', credits: 623, cost: 1557, queries: 1234 },
    { date: 'Sun', credits: 412, cost: 1030, queries: 876 },
  ];

  // Mock data - Top warehouses
  const topWarehouses = [
    { 
      name: 'ANALYTICS_WH', 
      size: 'X-Large',
      credits: 3456, 
      cost: 8640, 
      utilization: 23,
      status: 'over-provisioned',
      recommendation: 'Downsize to Large',
      savings: 4320
    },
    { 
      name: 'ETL_WH', 
      size: 'Large',
      credits: 2134, 
      cost: 5335, 
      utilization: 78,
      status: 'optimal',
      recommendation: null,
      savings: 0
    },
    { 
      name: 'REPORTING_WH', 
      size: 'Medium',
      credits: 1567, 
      cost: 3917, 
      utilization: 45,
      status: 'under-utilized',
      recommendation: 'Downsize to Small',
      savings: 1200
    },
    { 
      name: 'DATA_SCIENCE_WH', 
      size: 'X-Large',
      credits: 1234, 
      cost: 3085, 
      utilization: 89,
      status: 'optimal',
      recommendation: null,
      savings: 0
    },
    { 
      name: 'DEV_WH', 
      size: 'Small',
      credits: 456, 
      cost: 1140, 
      utilization: 12,
      status: 'over-provisioned',
      recommendation: 'Enable auto-suspend',
      savings: 800
    },
  ];

  // Mock data - Top users by cost
  const topUsers = [
    { name: 'data_team@company.com', role: 'DATA_ENGINEER', credits: 2345, cost: 5862, queries: 1234, trend: 'up' },
    { name: 'john.smith@company.com', role: 'ANALYST', credits: 1876, cost: 4690, queries: 892, trend: 'up' },
    { name: 'analytics_svc', role: 'SERVICE_ACCOUNT', credits: 1654, cost: 4135, queries: 15678, trend: 'stable' },
    { name: 'jane.doe@company.com', role: 'DATA_SCIENTIST', credits: 987, cost: 2467, queries: 234, trend: 'down' },
    { name: 'etl_pipeline', role: 'SERVICE_ACCOUNT', credits: 876, cost: 2190, queries: 8765, trend: 'stable' },
  ];

  // Mock data - Top expensive queries - only for demo
  const topQueries = shouldShowData ? [
    { 
      id: 'Q-7834',
      query: 'SELECT * FROM fact_sales JOIN dim_products ON ... (complex aggregation)',
      user: 'john.smith@company.com',
      warehouse: 'ANALYTICS_WH',
      credits: 234.5,
      cost: 586.25,
      duration: '45m 23s',
      rows: '2.3B',
      status: 'completed'
    },
    { 
      id: 'Q-7821',
      query: 'CREATE TABLE analytics.monthly_report AS SELECT ...',
      user: 'data_team@company.com',
      warehouse: 'ETL_WH',
      credits: 187.2,
      cost: 468.00,
      duration: '32m 11s',
      rows: '890M',
      status: 'completed'
    },
    { 
      id: 'Q-7819',
      query: 'INSERT INTO warehouse.aggregated_metrics SELECT ...',
      user: 'etl_pipeline',
      warehouse: 'ETL_WH',
      credits: 156.8,
      cost: 392.00,
      duration: '28m 45s',
      rows: '1.2B',
      status: 'completed'
    },
  ] : [];

  // Cost breakdown by category - only for demo or real data
  const costBreakdown = shouldShowData ? [
    { name: 'Compute', value: 18500, color: '#10b981' },
    { name: 'Storage', value: 3200, color: '#3b82f6' },
    { name: 'Data Transfer', value: 1800, color: '#f59e0b' },
    { name: 'Cloud Services', value: 900, color: '#06b6d4' },
  ] : [];

  // Use real data if available, otherwise mock
  const displayData = useMemo(() => {
    if (realData) {
      // Transform real data for display
      const dailyTrend = realData.costSummary?.daily_trend || [];
      const warehouseSummary = realData.warehouseMetering?.warehouse_summary || [];
      const users = realData.userCosts?.user_costs || [];
      
      return {
        creditUsageData: dailyTrend.map(d => ({
          date: new Date(d.DAY).toLocaleDateString('en-US', { weekday: 'short' }),
          credits: d.TOTAL_CREDITS || 0,
          cost: (d.TOTAL_CREDITS || 0) * 2.5, // Approximate cost
          queries: 0
        })),
        topWarehouses: warehouseSummary.map(w => ({
          name: w.WAREHOUSE_NAME,
          size: 'Variable',
          credits: w.TOTAL_CREDITS || 0,
          cost: (w.TOTAL_CREDITS || 0) * 2.5,
          utilization: 50, // Would need load history for actual
          status: 'optimal',
          recommendation: null,
          savings: 0
        })),
        topUsers: users.map(u => ({
          name: u.USER_NAME,
          role: 'USER',
          credits: u.TOTAL_CREDITS || 0,
          cost: (u.TOTAL_CREDITS || 0) * 2.5,
          queries: u.QUERY_COUNT || 0,
          trend: 'stable'
        })),
        totalCredits: realData.costSummary?.total_credits || 0,
        costByService: realData.costSummary?.cost_by_service || []
      };
    }
    
    // Return mock data only for demo users
    if (shouldShowData) {
      return {
        creditUsageData,
        topWarehouses,
        topUsers,
        totalCredits: creditUsageData.reduce((sum, d) => sum + d.credits, 0),
        costByService: []
      };
    }
    
    // Return empty data for regular users
    return null;
  }, [realData, creditUsageData, topWarehouses, topUsers, shouldShowData]);

  // Calculate derived values - hooks must be called unconditionally
  const totalCredits = displayData?.totalCredits || 0;
  const totalCost = totalCredits * 2.5; // $2.50 per credit approximate
  const totalQueries = useMemo(() => {
    if (!displayData?.creditUsageData) return 0;
    return displayData.creditUsageData.reduce((sum, d) => sum + (d.queries || 0), 0);
  }, [displayData]);
  const potentialSavings = useMemo(() => {
    if (!displayData?.topWarehouses) return 0;
    return displayData.topWarehouses.reduce((sum, w) => sum + (w.savings || 0), 0);
  }, [displayData]);

  // Show empty state for regular users without data
  if (!displayData && !loading) {
    return (
      <div className="space-y-6" data-testid="snowflake-dashboard">
        <EmptyDataState title="Snowflake Intelligence" description="Connect your Snowflake account to see credit usage, warehouse performance, and cost optimization insights." />
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'optimal': return 'text-emerald-500';
      case 'over-provisioned': return 'text-amber-500';
      case 'under-utilized': return 'text-emerald-500';
      default: return 'text-slate-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'optimal': return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'over-provisioned': return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'under-utilized': return <MinusCircle className="w-4 h-4 text-emerald-500" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="snowflake-dashboard">
      {/* Connection Modal */}
      <SnowflakeConnect 
        isOpen={showConnect} 
        onClose={() => setShowConnect(false)}
        onConnected={handleConnected}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
            <Snowflake className="w-6 h-6 text-slate-950" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white"><BrandTitle>Snowflake Cost Intelligence</BrandTitle></h1>
            <p className="text-slate-400 dark:text-slate-400">
              {isConnected ? (
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  Connected to real Snowflake data
                </span>
              ) : (
                'Real-time credit usage and optimization insights'
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Connection Status */}
          {isConnected ? (
            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              <CheckCircle className="w-3 h-3 mr-1" />
              Live Data
            </Badge>
          ) : (
            <Badge variant="outline" className="text-amber-600 border-amber-300">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Demo Data
            </Badge>
          )}
          
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            {['24h', '7d', '30d', '90d'].map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  timeRange === range
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-400 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing || loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${(refreshing || loading) ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {isConnected ? (
            <Button variant="outline" size="sm" onClick={disconnect} className="text-red-500 border-red-200">
              <XCircle className="w-4 h-4 mr-2" />
              Disconnect
            </Button>
          ) : (
            <Button size="sm" onClick={() => setShowConnect(true)} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950">
              <Plug className="w-4 h-4 mr-2" />
              Connect Snowflake
            </Button>
          )}
          <Link to="/snowflake-queries">
            <Button size="sm" variant="outline">
              <Search className="w-4 h-4 mr-2" />
              Query Explorer
            </Button>
          </Link>
        </div>
      </div>

      {/* Connect Prompt - Show when not connected */}
      {!isConnected && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center">
                <Database className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white">Connect Your Snowflake Account</h3>
                <p className="text-sm text-slate-400 dark:text-slate-400">
                  View real-time credit usage, query costs, and storage data. <strong>Your credentials stay in your browser only.</strong>
                </p>
              </div>
            </div>
            <Button onClick={() => setShowConnect(true)} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950">
              <Plug className="w-4 h-4 mr-2" />
              Connect Now
            </Button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
          <span className="ml-3 text-slate-400">Fetching data from Snowflake...</span>
        </div>
      )}

      {/* Alert Banner - only show for demo/connected users with data */}
      {!loading && displayData && (
      <>
      {shouldShowData && (
      <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500 shadow-lg shadow-amber-500/40 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-medium text-slate-900 dark:text-white">Credit Spike Detected</p>
            <p className="text-sm text-slate-400 dark:text-slate-400">
              Thursday's usage was 67% higher than average. Caused by ANALYTICS_WH running unoptimized queries.
            </p>
          </div>
        </div>
        <Link to="/snowflake-alerts">
          <Button variant="outline" size="sm" className="border-amber-500/30 text-amber-600 hover:bg-amber-500/10">
            View Details
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md bg-emerald-50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Total Credits (7d)</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{totalCredits.toLocaleString()}</p>
                <div className="flex items-center gap-1 mt-2 text-amber-600">
                  {totalCredits > 0 ? (
                    <>
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-sm font-medium">+12.3% vs last week</span>
                    </>
                  ) : (
                    <span className="text-sm text-slate-400">No data yet</span>
                  )}
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center">
                <Snowflake className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-emerald-50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Total Cost (7d)</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">${totalCost.toLocaleString()}</p>
                <div className="flex items-center gap-1 mt-2 text-emerald-600">
                  {totalCost > 0 ? (
                    <>
                      <TrendingDown className="w-4 h-4" />
                      <span className="text-sm font-medium">-5.2% vs last week</span>
                    </>
                  ) : (
                    <span className="text-sm text-slate-400">No data yet</span>
                  )}
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-emerald-50 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Total Queries (7d)</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1">{totalQueries.toLocaleString()}</p>
                <div className="flex items-center gap-1 mt-2 text-slate-400">
                  <Activity className="w-4 h-4" />
                  <span className="text-sm font-medium">{totalQueries > 0 ? `Avg $${(totalCost / (totalQueries || 1)).toFixed(2)}/query` : 'No queries yet'}</span>
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center">
                <Database className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md bg-amber-50 bg-gradient-to-br from-amber-50 to-white dark:from-amber-900/20 dark:to-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400 dark:text-slate-400">Potential Savings</p>
                <p className="text-3xl font-bold text-amber-600 mt-1">${potentialSavings.toLocaleString()}</p>
                <div className="flex items-center gap-1 mt-2 text-amber-600">
                  {potentialSavings > 0 ? (
                    <>
                      <Lightbulb className="w-4 h-4" />
                      <span className="text-sm font-medium">{displayData?.topWarehouses?.filter(w => w.recommendation).length || 0} optimizations found</span>
                    </>
                  ) : (
                    <span className="text-sm text-slate-400">No optimizations found</span>
                  )}
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-500 shadow-lg shadow-amber-500/40 flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Credit Usage Chart */}
        <Card className="lg:col-span-2 border-0 shadow-md">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Credit Usage Trend</CardTitle>
                <CardDescription>Daily credit consumption over the past week</CardDescription>
              </div>
              <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                <AlertTriangle className="w-3 h-3 mr-1" />
                Spike on Thu
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                <AreaChart data={displayData.creditUsageData}>
                  <defs>
                    <linearGradient id="creditGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1e293b', 
                      border: 'none', 
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                    formatter={(value, name) => [
                      name === 'credits' ? `${value.toLocaleString()} credits` : `$${value.toLocaleString()}`,
                      name === 'credits' ? 'Credits' : 'Cost'
                    ]}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="credits" 
                    stroke="#06b6d4" 
                    strokeWidth={2}
                    fill="url(#creditGradient)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Cost Breakdown Pie */}
        <Card className="border-0 shadow-md">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Cost Breakdown</CardTitle>
            <CardDescription>By service category</CardDescription>
          </CardHeader>
          <CardContent>
            {costBreakdown.length > 0 ? (
            <>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                <RechartsPie>
                  <Pie
                    data={costBreakdown}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={4}
                    dataKey="value"
                  >
                    {costBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, 'Cost']}
                    contentStyle={{ 
                      backgroundColor: '#1e293b', 
                      border: 'none', 
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                  />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {costBreakdown.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-xs text-slate-400 dark:text-slate-400">{item.name}</span>
                  <span className="text-xs font-medium text-slate-900 dark:text-white ml-auto">${item.value.toLocaleString()}</span>
                </div>
              ))}
            </div>
            </>
            ) : (
              <div className="flex flex-col items-center justify-center h-72 text-slate-400">
                <PieChart className="w-12 h-12 mb-2" />
                <p className="text-sm">Connect Snowflake to see cost breakdown</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Warehouse Efficiency */}
      <Card className="border-0 shadow-md">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Server className="w-5 h-5 text-emerald-500" />
                Warehouse Efficiency Scores
              </CardTitle>
              <CardDescription>Utilization analysis and optimization recommendations</CardDescription>
            </div>
            <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              ${potentialSavings.toLocaleString()}/mo savings available
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {displayData.topWarehouses.map((warehouse) => (
              <div key={warehouse.name} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center">
                      <Database className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">{warehouse.name}</p>
                      <p className="text-sm text-slate-400">Size: {warehouse.size}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm text-slate-400">Credits Used</p>
                      <p className="font-semibold text-slate-900 dark:text-white">{warehouse.credits.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-400">Cost</p>
                      <p className="font-semibold text-slate-900 dark:text-white">${warehouse.cost.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-400 dark:text-slate-400">Utilization</span>
                      <span className={`text-sm font-medium ${getStatusColor(warehouse.status)}`}>
                        {warehouse.utilization}%
                      </span>
                    </div>
                    <Progress value={warehouse.utilization} className="h-2" />
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(warehouse.status)}
                    <span className={`text-sm font-medium capitalize ${getStatusColor(warehouse.status)}`}>
                      {warehouse.status.replace('-', ' ')}
                    </span>
                  </div>
                  {warehouse.recommendation && (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                      <Lightbulb className="w-4 h-4 text-amber-500" />
                      <span className="text-sm text-amber-700 dark:text-amber-400">{warehouse.recommendation}</span>
                      <span className="text-sm font-semibold text-amber-600">Save ${warehouse.savings.toLocaleString()}/mo</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top Users and Top Queries */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Users */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-500" />
                  Top Users by Cost
                </CardTitle>
                <CardDescription>Credit consumption by user/service account</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {displayData.topUsers.map((user, idx) => (
                <div key={user.name} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-slate-950 text-sm font-bold">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 dark:text-white truncate">{user.name}</p>
                    <p className="text-xs text-slate-400">{user.role}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-slate-900 dark:text-white">${user.cost.toLocaleString()}</p>
                    <p className="text-xs text-slate-400">{user.credits.toLocaleString()} credits</p>
                  </div>
                  {user.trend === 'up' && <TrendingUp className="w-4 h-4 text-red-500" />}
                  {user.trend === 'down' && <TrendingDown className="w-4 h-4 text-emerald-500" />}
                  {user.trend === 'stable' && <Activity className="w-4 h-4 text-slate-400" />}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Top Queries */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Database className="w-5 h-5 text-amber-500" />
                  Most Expensive Queries
                </CardTitle>
                <CardDescription>Top 3 queries by credit consumption</CardDescription>
              </div>
              <Link to="/snowflake-queries">
                <Button variant="outline" size="sm">
                  View All
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {topQueries.length > 0 ? (
            <div className="space-y-3">
              {topQueries.map((query, idx) => (
                <div key={query.id} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{query.id}</Badge>
                      <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 text-xs">
                        {query.warehouse}
                      </Badge>
                    </div>
                    <p className="font-bold text-amber-600">${query.cost.toFixed(2)}</p>
                  </div>
                  <p className="text-sm text-slate-400 dark:text-slate-400 truncate mb-2 font-mono">
                    {query.query}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {query.user}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {query.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <Database className="w-3 h-3" />
                      {query.rows} rows
                    </span>
                  </div>
                </div>
              ))}
            </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                <Database className="w-12 h-12 mb-2" />
                <p className="text-sm">Connect Snowflake to see query costs</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border-0 shadow-md bg-emerald-500 text-slate-950">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold mb-2">Ready to optimize your Snowflake costs?</h3>
              <p className="text-emerald-950/70">
                We found ${potentialSavings.toLocaleString()}/month in potential savings across 3 warehouses.
              </p>
            </div>
            <div className="flex gap-3">
              <Link to="/snowflake-queries">
                <Button className="bg-slate-950 text-emerald-300 hover:bg-slate-900">
                  <Search className="w-4 h-4 mr-2" />
                  Explore Queries
                </Button>
              </Link>
              <Link to="/snowflake-alerts">
                <Button variant="outline" className="border-slate-950/30 text-slate-950 hover:bg-slate-950/10">
                  <Bell className="w-4 h-4 mr-2" />
                  Set Up Alerts
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
};

export default SnowflakeDashboard;
